import type { LeaseComp } from "@/features/leaseComps/types/leaseComps.types";
import type { Property, Survey } from "@/features/survey/types/survey.types";

type CombinedLegendItem =
  | { kind: "property"; item: Property }
  | { kind: "lease"; item: LeaseComp };

export interface LegendPageData {
  pageProperties: Property[];
  pageLeaseComps: LeaseComp[];
  numberedPropertiesBeforePage: number;
  startLeaseIndexFrom: number;
  hasPageContent: boolean;
}

interface UseMarketMapLegendPagesReturn {
  totalItemsCount: number;
  totalPages: number;
  estimatedLegendHeight: number;
  getPageData: (pageIndex: number) => LegendPageData;
}

export const useMarketMapLegendPages = (
  survey: Survey | null,
  visibleProperties: Property[],
  visibleLeaseComps: LeaseComp[],
): UseMarketMapLegendPagesReturn => {
  const isPortrait = survey?.pdfPaperSize === "Portrait";
  const hasHeader = !!survey?.isIncludeHeader;

  const ITEMS_PER_PAGE = isPortrait
    ? hasHeader
      ? 14
      : 18
    : hasHeader
      ? 14
      : 16;

  const combinedLegendItems: CombinedLegendItem[] = [
    ...visibleProperties.map((p) => ({ kind: "property" as const, item: p })),
    ...visibleLeaseComps.map((lc) => ({ kind: "lease" as const, item: lc })),
  ];

  const totalItemsCount = combinedLegendItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItemsCount / ITEMS_PER_PAGE));

  const firstPageItemsCount = Math.min(ITEMS_PER_PAGE, totalItemsCount);
  const displayFields = survey?.customLegends?.displayFields;
  const hasOtherFields = !!(
    displayFields?.hasAddressLine1 ||
    displayFields?.hasCity ||
    displayFields?.hasState ||
    displayFields?.hasZipCode
  );
  const linesPerItem = displayFields?.hasPropertyName
    ? hasOtherFields
      ? 2
      : 1
    : hasOtherFields
      ? 1
      : 0;
  const legendRows = Math.ceil(firstPageItemsCount / 2);
  const estimatedLegendHeight = survey?.isIncludeLegend
    ? legendRows * (linesPerItem <= 1 ? 33.25 : 52.75) + 32
    : 0;

  const getPageData = (pageIndex: number): LegendPageData => {
    const startIndex = pageIndex * ITEMS_PER_PAGE;
    const endIndex = Math.min(
      startIndex + ITEMS_PER_PAGE,
      combinedLegendItems.length,
    );
    const pageItems = combinedLegendItems.slice(startIndex, endIndex);

    const pageProperties = pageItems
      .filter(
        (e): e is { kind: "property"; item: Property } => e.kind === "property",
      )
      .map((e) => e.item);

    const pageLeaseComps = pageItems
      .filter(
        (e): e is { kind: "lease"; item: LeaseComp } => e.kind === "lease",
      )
      .map((e) => e.item);

    const itemsBeforePage = combinedLegendItems.slice(0, startIndex);
    const numberedPropertiesBeforePage = itemsBeforePage
      .filter(
        (e): e is { kind: "property"; item: Property } => e.kind === "property",
      )
      .filter((e) => !e.item.isCurrentLocation).length;

    const startLeaseIndexFrom = itemsBeforePage.filter(
      (e) => e.kind === "lease",
    ).length;

    return {
      pageProperties,
      pageLeaseComps,
      numberedPropertiesBeforePage,
      startLeaseIndexFrom,
      hasPageContent: pageProperties.length > 0 || pageLeaseComps.length > 0,
    };
  };

  return { totalItemsCount, totalPages, estimatedLegendHeight, getPageData };
};
